import { api } from "$lib/helpers/api.service";
import type {
  PoiDetail,
  PoiItem,
  PoiRating,
  RatingsResponse,
} from "$lib/types";
import { BaseStore } from "$lib/stores/base.svelte";
import { cache } from "$lib/helpers/cache.service";

class PoiDetailsStore extends BaseStore {
  private static readonly CACHE_NAME = "poi-details";

  private poiItems: PoiItem[] = $state([]);
  public currentIndex = $state(0);
  currentPoiDetail = $state<PoiDetail | null>(null);

  // Ratings state
  ratings = $state<PoiRating[]>([]);
  ratingsOffset = $state(0);
  ratingsLimit = 10;
  ratingsHasMore = $state(false);
  ratingsLoading = $state(false);

  totalCount = $derived(this.poiItems.length);
  canGoPrev = $derived(this.currentIndex > 0);
  canGoNext = $derived(this.currentIndex < this.poiItems.length - 1);
  currentPoiId = $derived(
    this.poiItems.length > 0 ? this.poiItems[this.currentIndex] : null
  );
  currentPosition = $derived(this.currentIndex + 1);

  constructor() {
    super();
    cache.createCache(PoiDetailsStore.CACHE_NAME, { ttl: 5 * 60 * 1000 });
  }

  async select(poiItems: PoiItem[]) {
    if (poiItems.length === 0) {
      this.clear();
      return;
    }

    this.poiItems = [...poiItems];
    this.currentIndex = 0;
    await this.loadCurrentPoi();
  }

  clear() {
    this.poiItems = [];
    this.currentIndex = 0;
    this.currentPoiDetail = null;
    this.error = null;

    this.ratings = [];
    this.ratingsOffset = 0;
    this.ratingsHasMore = false;
  }

  async navigate(direction: "prev" | "next" | number) {
    if (direction === "prev" && !this.canGoPrev) return;
    if (direction === "next" && !this.canGoNext) return;

    if (typeof direction === "number") {
      if (direction < 0 || direction >= this.poiItems.length) return;
      this.currentIndex = direction;
    } else {
      this.currentIndex += direction === "next" ? 1 : -1;
    }

    await this.loadCurrentPoi();
  }

  private async loadCurrentPoi() {
    if (!this.currentPoiId) return;

    const cached = cache.get<PoiDetail>(
      PoiDetailsStore.CACHE_NAME,
      this.currentPoiId.toString()
    );

    if (cached) {
      this.currentPoiDetail = cached;
      this.preloadNext();
      this.loadRatingsIfAvailable();
      return;
    }

    const detail = await this.executeWithLoading(
      () => this.fetchPoiDetail(this.currentPoiId!),
      `Failed to load POI ${this.currentPoiId}`
    );
    this.isLoading = false;

    if (detail) {
      this.currentPoiDetail = detail;
      this.preloadNext();
      this.loadRatingsIfAvailable();
    }
  }

  private async fetchPoiDetail(poi: PoiItem): Promise<PoiDetail> {
    const id = poi.id;
    const cached = cache.get<PoiDetail>(
      PoiDetailsStore.CACHE_NAME,
      id.toString()
    );

    if (cached) return cached;

    let detail = await api.get<PoiDetail>(`/point-of-interests/${id}/details`);

    detail = { ...detail, hasCoupon: poi.hasCoupon };

    cache.set(PoiDetailsStore.CACHE_NAME, id.toString(), detail);
    return detail;
  }

  private async loadRatingsIfAvailable() {
    if (
      this.currentPoiDetail &&
      this.currentPoiDetail.rating != null &&
      this.currentPoiDetail.ratingNumber != null &&
      this.currentPoiDetail.ratingNumber > 0
    ) {
      this.ratings = [];
      this.ratingsOffset = 0;
      await this.loadMoreRatings();
    } else {
      this.ratings = [];
      this.ratingsOffset = 0;
      this.ratingsHasMore = false;
    }
  }

  async loadMoreRatings() {
    if (!this.currentPoiDetail || this.ratingsLoading) return;

    this.ratingsLoading = true;
    try {
      const res = await api.get<RatingsResponse>(
        `/point-of-interests/${this.currentPoiDetail.id}/ratings?offset=${this.ratingsOffset}&limit=${this.ratingsLimit}`
      );

      if (res.items.length > 0) {
        this.ratings = [...this.ratings, ...res.items];
        this.ratingsOffset += res.items.length;
        this.ratingsHasMore = res.items.length === this.ratingsLimit;
      } else {
        this.ratingsHasMore = false;
      }
    } catch (err) {
      console.error("Failed to load ratings:", err);
    } finally {
      this.ratingsLoading = false;
    }
  }

  private async preloadNext() {
    if (!this.canGoNext) return;

    const nextId = this.poiItems[this.currentIndex + 1];
    if (cache.has(PoiDetailsStore.CACHE_NAME, nextId.toString())) return;

    try {
      await this.fetchPoiDetail(nextId);
    } catch (error) {
      console.warn(`Failed to preload POI ${nextId}:`, error);
    }
  }

  async refresh() {
    if (!this.currentPoiId) return;
    cache.clear(PoiDetailsStore.CACHE_NAME);
    await this.loadCurrentPoi();
  }
}

export const detailsStore = new PoiDetailsStore();
